import React, { useEffect, useState } from 'react';
import { Card, Button, Typography, Space, Spin, Alert } from 'antd';
import { PlusOutlined, BellOutlined, ReloadOutlined } from '@ant-design/icons';
import { useChannelStore } from '../../store/channelStore';
import { useWeb3Auth } from '../../hooks/useWeb3Auth';
import ChannelTable from './ChannelTable';
import ChannelFormModal from './ChannelFormModal';
import TestChannelModal from './TestChannelModal';
import Web3AuthPage from './Web3AuthPage';

const { Title, Paragraph } = Typography;

/**
 * 通知渠道管理主页面
 * 包含渠道列表、新建/编辑渠道、测试渠道等功能
 */
const ChannelListPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading, validateAuth } = useWeb3Auth();
  const {
    channels,
    loading,
    error,
    isFormModalVisible,
    isTestModalVisible,
    fetchChannels,
    openFormModal,
    clearError,
  } = useChannelStore();

  // 用于强制重新渲染的状态
  const [renderKey, setRenderKey] = useState(0);

  // 监听认证状态变化，强制重新渲染
  useEffect(() => {
    setRenderKey(prev => prev + 1);
  }, [isAuthenticated]);

  // 加载渠道列表
  useEffect(() => {
    if (isAuthenticated) {
      // 先验证认证状态，然后加载数据
      validateAuth().then(isValid => {
        if (isValid) {
          fetchChannels();
        }
      });
    }
  }, [isAuthenticated, fetchChannels, validateAuth]);

  // 如果未认证，显示认证页面
  if (!isAuthenticated) {
    return <Web3AuthPage key={`auth-${renderKey}`} />;
  }

  // 处理刷新
  const handleRefresh = () => {
    fetchChannels();
  };

  // 处理新建渠道
  const handleCreateChannel = () => {
    openFormModal();
  };

  return (
    <div className="p-6">
      <Spin spinning={loading || authLoading}>
        {/* 页面标题和操作按钮 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Title level={2} className="mb-2">
              <BellOutlined className="mr-3" />
              通知渠道管理
            </Title>
            <Paragraph type="secondary" className="mb-0">
              管理您的通知渠道，支持 Telegram、Discord、钉钉、飞书、邮件等多种平台
            </Paragraph>
          </div>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleRefresh}
              loading={loading}
            >
              刷新
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreateChannel}
              size="large"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
              }}
            >
              新建渠道
            </Button>
          </Space>
        </div>

        {/* 错误提示 */}
        {error && (
          <Alert
            message="操作失败"
            description={error}
            type="error"
            showIcon
            closable
            onClose={clearError}
            className="mb-4"
          />
        )}

        {/* 渠道统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {channels.length}
            </div>
            <div className="text-gray-500">总渠道数</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {channels.filter(c => c.is_enabled).length}
            </div>
            <div className="text-gray-500">已启用</div>
          </Card>
          <Card className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {channels.filter(c => !c.is_enabled).length}
            </div>
            <div className="text-gray-500">已禁用</div>
          </Card>
        </div>

        {/* 渠道列表表格 */}
        <Card>
          <ChannelTable />
        </Card>

        {/* 新建/编辑渠道模态框 */}
        {isFormModalVisible && <ChannelFormModal />}

        {/* 测试渠道模态框 */}
        {isTestModalVisible && <TestChannelModal />}
      </Spin>
    </div>
  );
};

export default ChannelListPage;
